import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import Select, { Option } from "../../components/Select";
import { newCompanyMonitoringFormValidation } from "../../utils/validationSchema";
import { DatePicker } from "../../components/DatePicker";
import { Form, Formik } from "formik";
import { NewCompanyMonitoringRecordFrame } from "./NewCompanyMonitoringRecordFrame";
import { MonitoringEventsFields } from "../../components/MonitoringEventsFields";
import { useCountries } from "../../data/useCountries";
import { useEntityTypes } from "../../data/useEntityTypes";
import { useMembershipDivisionTeam } from "../../hooks/useMembershipDivisionTeam";

const rowProps: any = {
  flexWrap: "wrap",
  gap: "16",
  my: "4",
};

const inputProps = {
  minWidth: 200,
  flex: 1,
};

interface MonitoringCompanyForm {
  yourReference: string;
  division: number | string;
  team: number | string;
  company_name: string;
  country: number | null;
  entity_type: number | null;
  registration_number: string;
  date_of_incorporation: Date | null;
  pep_and_sanction_required: boolean;
  adverse_media_required: boolean;
}

const Header = () => {
  return (
    <>
      <H2>Company Details</H2>
      <Paragraph>Please add the company to perform the screening on.</Paragraph>
    </>
  );
};

export const NewComapnyMonitoring = () => {
  const [doubleClick, setDoubleClick] = useState(false);
  const navigate = useNavigate();
  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const entityTypeOptions = entityTypes?.data.map(({ id, name }) => ({
    label: name,
    value: id,
  })) as Option[];

  const countryOptions = countries?.data.map(({ id, name }) => ({
    label: name,
    value: id,
  })) as Option[];

  const handleSubmit = (form: MonitoringCompanyForm) => {
    setDoubleClick(true);

    navigate(`/monitoring/company/new/confirm`, {
      state: { form },
    });
  };

  return (
    <>
      <NewCompanyMonitoringRecordFrame step="company">
        <Formik
          initialValues={{
            yourReference: "",
            division: singleDivisionId,
            team: singleTeamId,
            company_name: "",
            country: null,
            entity_type: null,
            registration_number: "",
            date_of_incorporation: null,
            pep_and_sanction_required: null,
            adverse_media_required: null,
          }}
          validationSchema={newCompanyMonitoringFormValidation}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit} style={{ width: "510px" }}>
              <Header />

              <Flex {...rowProps}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.yourReference}
                  placeholder="Your Reference"
                  label="Your reference"
                  name="yourReference"
                />
              </Flex>

              {showDivisionField && (
                <Flex {...rowProps}>
                  <Select
                    {...inputProps}
                    onChange={(option: any) => {
                      const newDivisionTeams = teamOptionsFor(option.value);
                      props.setValues({
                        ...props.values,
                        division: option.value,
                        team:
                          newDivisionTeams.length === 1
                            ? newDivisionTeams[0].value
                            : "",
                      });
                    }}
                    value={divisionOptions?.find(
                      ({ value }: any) => value === props.values.division
                    )}
                    options={divisionOptions}
                    placeholder="Choose division"
                    label="Division"
                    name="division"
                    isRequired
                    hasError={Boolean(props.errors.division)}
                    errorMessage={props.errors.division as string}
                  />
                </Flex>
              )}
              {teamOptionsFor(props.values.division).length > 0 && (
                <Flex {...rowProps}>
                  <Select
                    {...inputProps}
                    key={props.values.division}
                    onChange={(option: any) =>
                      props.setFieldValue("team", option.value)
                    }
                    value={
                      teamOptionsFor(props.values.division).find(
                        ({ value }: any) => value === props.values.team
                      ) ?? null
                    }
                    options={teamOptionsFor(props.values.division)}
                    placeholder="Choose team"
                    label="Team"
                    name="team"
                    isRequired
                    hasError={Boolean(props.errors.team)}
                    errorMessage={props.errors.team as string}
                  />
                </Flex>
              )}

              <Flex {...rowProps}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.company_name}
                  placeholder="Company name"
                  label="Company name"
                  name="company_name"
                  isRequired
                  hasError={Boolean(props.errors.company_name)}
                  errorMessage={props.errors.company_name as string}
                />
              </Flex>

              <Flex {...rowProps}>
                <Select
                  value={countryOptions?.find(
                    ({ value }) => value === props.values.country
                  )}
                  options={countryOptions}
                  onChange={(option: Option) => {
                    props.setFieldValue("country", option.value);
                  }}
                  minWidth={200}
                  flex={1}
                  label="Country of registration/establishment"
                  name="country"
                  isRequired
                  hasError={Boolean(props.errors.country)}
                  errorMessage={props.errors.country as string}
                />
              </Flex>

              <Flex {...rowProps}>
                <Select
                  value={entityTypeOptions?.find(
                    ({ value }) => value === props.values.entity_type
                  )}
                  options={entityTypeOptions}
                  onChange={(option: Option) =>
                    props.setFieldValue("entity_type", option.value)
                  }
                  minWidth={200}
                  flex={1}
                  label="Company type"
                  name="entity_type"
                  hasError={Boolean(props.errors.entity_type)}
                  errorMessage={props.errors.entity_type as string}
                />
              </Flex>

              <Flex {...rowProps}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.registration_number}
                  placeholder="Registration number"
                  mt="2"
                  label="Registration number"
                  name="registration_number"
                  hasError={Boolean(props.errors.registration_number)}
                  errorMessage={props.errors.registration_number as string}
                />

                <DatePicker
                  value={props.values.date_of_incorporation as any}
                  onChange={(date: any) =>
                    props.setFieldValue("date_of_incorporation", date)
                  }
                  mt="2"
                  label="Date of incorporation"
                  name="date_of_incorporation"
                  hasError={Boolean(props.errors.date_of_incorporation)}
                  errorMessage={props.errors.date_of_incorporation as string}
                  {...inputProps}
                />
              </Flex>

              <MonitoringEventsFields
                formikProps={props}
                rowProps={rowProps}
                pepLabel="Sanctions"
                adverseLabel="Entity Adverse Media"
                intro={
                  <>
                    <H3>Monitoring Events</H3>
                    <Paragraph>
                      Please select the events you would like to monitor.
                    </Paragraph>
                  </>
                }
              />

              <Flex justifyContent="space-between" mt="4">
                <Button
                  size="large"
                  mt="2"
                  mb="2"
                  variant="secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  size="large"
                  mt="2"
                  mb="2"
                  type="submit"
                  isDisabled={(props.dirty && !props.isValid) || doubleClick}
                >
                  Next
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </NewCompanyMonitoringRecordFrame>
    </>
  );
};
